import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly, differenceInDays } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency, EPaymentMethod } from '@if/core/types'
import { array, dateOnly, InferType, object, ref, string } from '@if/core/yup'
import { useUser } from '@src/providers/UserProvider'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { User } from '@src/types/user'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'

export const getMoveCalendarSchema = (canBypassAcpLock: boolean) =>
  object({
    fromDate: dateOnly()
      .required()
      .test(
        'test-zeroShiftCase',
        () => t('loan.payment.moveCalendarDialog.errors.zeroDayShift'),
        (value, context) => {
          const toDate = context.resolve<DateOnly>(ref('toDate'))
          if (!toDate || !value) return true // If the to date is null, we can't validate
          return value.getTime() !== toDate.getTime()
        },
      )
      .test(
        'test-validMinimumFromDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidFromDate'),
        (value, context) => {
          if (!value) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const minimumDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedPayment, canBypassAcpLock)
          return value >= minimumDate
        },
      )
      .test(
        'test-validPaymentDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidPaymentDate'),
        (value, context) => {
          if (!value) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const operations = simulation.operationInformations
          if (!operations) return true
          return operations.some(
            (oi) => oi.isCustomerPaymentOperation() && oi.operation.effectiveDate.getTime() === value.getTime(),
          )
        },
      ),
    toDate: dateOnly()
      .required()
      .test(
        'test-validMinimumToDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidToDate'),
        (value, context) => {
          if (!value) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const minimumDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedPayment, canBypassAcpLock)
          return value >= minimumDate
        },
      )
      .test(
        'test-shiftBeforeActivationDate',
        () => t('loan.payment.moveCalendarDialog.errors.beforeActivationDateToDate'),
        (value, context) => {
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const activationDate = simulation.fullLoan.loan.activationOn

          return value > activationDate
        },
      )
      .test(
        'test-zeroShiftCase',
        () => t('loan.payment.moveCalendarDialog.errors.zeroDayShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          if (!fromDate || !value) return true // If the to date is null, we can't validate
          return value.getTime() !== fromDate.getTime()
        },
      )
      .test(
        'test-monthlyTooFarShift',
        () => t('loan.payment.moveCalendarDialog.errors.monthlyTooFarShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))

          if (!fromDate || !value || !simulation) return true // If the to date or simulation is null, we can't validate
          if (simulation.getRepaymentConfigAtDate(fromDate)?.frequency !== EPaymentFrequency.Monthly) return true
          return differenceInDays(value, fromDate) <= 30
        },
      )
      .test(
        'test-biweeklyTooFarShift',
        () => t('loan.payment.moveCalendarDialog.errors.biweeklyTooFarShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))

          if (!fromDate || !value || !simulation) return true // If the to date or simulation is null, we can't validate
          if (simulation.getRepaymentConfigAtDate(fromDate)?.frequency !== EPaymentFrequency.BiWeekly) return true
          return differenceInDays(value, fromDate) <= 7
        },
      )
      .test(
        'test-movingBeforeNextPayment',
        () => t('loan.payment.moveCalendarDialog.errors.shiftAfterNextPayment'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))

          if (!fromDate || !value || !simulation) return true // If the to date or simulation is null, we can't validate
          const nextPayment = simulation.getNextPaymentAfterDate(fromDate)
          if (!nextPayment) return true

          return nextPayment.effectiveDate.getTime() >= value.getTime()
        },
      ),

    reason: string().required(),

    paymentToRaiseIds: array()
      .of(string().required())
      .required()
      .default([])
      .test(
        'test-unnecessaryPaymentToRaiseIds',
        () => t('loan.payment.moveCalendarDialog.errors.unnecessaryPaymentToRaiseIds'),
        (value, context) => {
          // No test needed if it's null
          if (value === null) return true

          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const toDate = context.resolve<DateOnly>(ref('toDate'))

          // If one of the date aren't valid, we return true since we can't test
          if (!fromDate || !toDate) return true

          // We're moving in the futur, so we don't need to check
          if (fromDate.getTime() < toDate.getTime()) return true

          return value.length === 0
        },
      )
      .test(
        'test-needsPaymentToRaiseIds',
        () => t('loan.payment.moveCalendarDialog.errors.needsPaymentToRaiseIds'),
        (value, context) => {
          // No test needed if it's null
          if (value === null) return true

          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const toDate = context.resolve<DateOnly>(ref('toDate'))
          const user = context.resolve<User | undefined>(ref('$user'))

          // If one of the date or the user aren't valid, we return true since we can't test
          if (!fromDate || !toDate || !user) return true

          // He can override this validation
          if (user.rights.canMoveCalendarWithLateAmount) return true

          // Moving to the past, so no need to have payments to raise
          if (fromDate.getTime() > toDate.getTime()) return true

          return value.length !== 0
        },
      ),
  })

export interface MoveCalendarSchemaContext {
  simulation: CalendarSimulation
}

export type MoveCalendarSchemaType = InferType<ReturnType<typeof getMoveCalendarSchema>>

export const useMoveCalendarResolver = () => {
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  return useMemo<Resolver<MoveCalendarSchemaType, MoveCalendarSchemaContext>>(
    () => yupResolver(getMoveCalendarSchema(canBypassAcpLock)),
    [canBypassAcpLock],
  )
}
